import { LocationCrosshairs } from "@styled-icons/fa-solid/LocationCrosshairs";
import { CloseCircle } from "@styled-icons/remix-fill/CloseCircle";
import { Filter2 } from "@styled-icons/remix-fill/Filter2";
import { Col, Flex, Grid, theme, Typography } from "antd";
import React, { useContext } from "react";

import { LocationFinderContext } from "./context";
import {
  FilterRow,
  LocationFinderFilterSelect,
  LocationListingsOverlayChild,
} from "./styled";
import { sortByBucketKey } from "./utils";

import { Geosuggest } from "~/Atom/Geosuggest";
import {
  GeosuggestContext,
  GeosuggestProvider,
} from "~/Atom/Geosuggest/context";
import { locateMe } from "~/Atom/Geosuggest/utils";
import { SearchIcon } from "~/Atom/SearchIcon";
import { SolutionIcon } from "~/Atom/SolutionIcon";
import { SolutionName } from "~/Atom/SolutionIcon/types";
import { Components } from "~/OmnichannelApi/openapi";
import { reverseGeocode } from "~/utils";

export const LocationSearch: React.FC = () => {
  const breakpoints = Grid.useBreakpoint();
  const { token } = theme.useToken();

  return (
    <LocationListingsOverlayChild $token={token} $breakpoints={breakpoints}>
      <GeosuggestProvider>
        <LocationSearchComponent />
      </GeosuggestProvider>
    </LocationListingsOverlayChild>
  );
};

export const LocationSearchComponent: React.FC = () => {
  const { token } = theme.useToken();
  const ctx = useContext(LocationFinderContext);
  const geosuggestCtx = useContext(GeosuggestContext);

  const locateMeCallbackFn = (position: GeolocationPosition) => {
    const latlon: google.maps.LatLngLiteral = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };

    reverseGeocode(latlon).then((res) => {
      geosuggestCtx.setValue(
        res.results.at(0)?.formatted_address ||
          `${position.coords.latitude}, ${position.coords.longitude}`
      );
    });

    ctx.setDistanceCenter(latlon);
    ctx.panMapTo(latlon);
  };

  const solutionBuckets =
    ctx.locationData?.aggs["location.markets.solution.name"]?.buckets?.sort(
      sortByBucketKey
    );

  const rentals: Components.Schemas.LocationFinderAgg["buckets"] = [];
  const retailSales: Components.Schemas.LocationFinderAgg["buckets"] = [];
  const maintenance: Components.Schemas.LocationFinderAgg["buckets"] = [];
  const dealershipBrands: Components.Schemas.LocationFinderAgg["buckets"] = [];

  ctx.locationData?.aggs[
    "location.markets.rental_services.name"
  ]?.buckets.forEach((b) => {
    if (/rental/i.test(b.key)) {
      rentals.push(b);
    } else if (/(sales|suppl(y|ies)|used)/i.test(b.key)) {
      retailSales.push(b);
    } else if (/repair/i.test(b.key)) {
      maintenance.push(b);
    } else {
      dealershipBrands.push(b);
    }
  });

  const locationOptions = [
    {
      label: "All Locations",
      title: "All Locations",
      value: "_all",
    },
    // {
    //   label: "Coming Soon (not working yet)",
    //   title: "coming_soon",
    //   value: "_coming_soon",
    // },
  ];

  const rentalServicesOptions = [
    {
      label: "Rental Services",
      title: "Rental Services",
      options:
        rentals?.sort(sortByBucketKey).map((i) => ({
          label: i.key,
          title: i.key,
          value: `rental_service:${i.key}`,
        })) || [],
    },
    {
      label: "Maintenance",
      title: "Maintenance",
      options:
        maintenance?.sort(sortByBucketKey).map((i) => ({
          label: i.key,
          title: i.key,
          value: `rental_service:${i.key}`,
        })) || [],
    },
    {
      label: "Retail Sales",
      title: "Retail Sales",
      options:
        retailSales?.sort(sortByBucketKey).map((i) => ({
          label: i.key,
          title: i.key,
          value: `rental_service:${i.key}`,
        })) || [],
    },
    {
      label: "Dealership Brands",
      title: "Dealership Brands",
      options:
        dealershipBrands?.sort(sortByBucketKey).map((i) => ({
          label: i.key,
          title: i.key,
          value: `rental_service:${i.key}`,
        })) || [],
    },
  ];

  return (
    <div
      style={{
        all: "unset",
      }}
    >
      <FilterRow $token={token}>
        <Col xs={24} id="locationFinderGeosuggest">
          <SearchIcon
            style={{
              height: "1.5rem",
              position: "absolute",
              zIndex: 1,
              marginLeft: "1rem",
              marginTop: "0.875rem",
            }}
          />
          <Geosuggest
            onSelect={(place) => {
              if (
                place?.geometry?.location?.lat &&
                place?.geometry?.location?.lng
              ) {
                const latlng: google.maps.LatLngLiteral = {
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng(),
                };

                ctx.setActiveMarkerSlug(undefined);
                ctx.setDistanceCenter(latlng);
                ctx.panMapTo(latlng);
              }
            }}
            autoCompleteProps={{
              "aria-label": "Search locations",
              defaultActiveFirstOption: true,
              allowClear: {
                clearIcon: (
                  <CloseCircle
                    style={{
                      height: "1.25rem",
                      width: "1.25rem",
                      marginTop: "-0.125rem",
                    }}
                  />
                ),
              },
              autoFocus: true,
              placeholder: "Search by address, city, or state",
              style: {
                display: "block",
              },
              notFoundContent: (
                <Flex
                  data-testid="use-current-location"
                  onClick={() => locateMe(locateMeCallbackFn)}
                  style={{
                    cursor: "pointer",
                  }}
                  justify="start"
                  align="center"
                  gap="small"
                >
                  <LocationCrosshairs
                    fill={token.colorPrimaryBg}
                    style={{
                      height: "1rem",
                      width: "1rem",
                      paddingTop: "0.125rem",
                    }}
                  />
                  <Typography.Text>{"Use Current Location"}</Typography.Text>
                </Flex>
              ),
            }}
            autoCompletePlaceTypes={["geocode"]}
          />
        </Col>
      </FilterRow>
      <FilterRow $token={token}>
        <Col xs={24} id="locationFinderFilter">
          <LocationFinderFilterSelect
            loading={!ctx.locationData}
            prefix={
              <Filter2
                fill={token.colorPrimary}
                style={{
                  height: "1.5rem",
                  paddingLeft: "0.25rem",
                  marginRight: "0.625rem",
                }}
              />
            }
            aria-label="Filter Locations"
            placeholder="Filter Locations"
            value={ctx.activeFilterTypeValue || undefined}
            onChange={(v) => ctx.setActiveFilterTypeValue(v as string)}
            options={[
              ...locationOptions,
              {
                label: "Solutions",
                title: "Solutions",
                options:
                  solutionBuckets?.map((i) => ({
                    label: (
                      <Flex gap="small" justify="flex-start" align="center">
                        <SolutionIcon solutionName={i.key as SolutionName} />
                        <span>{i.key}</span>
                      </Flex>
                    ),
                    title: i.key,
                    value: `solution:${i.key}`,
                  })) || [],
              },
              ...rentalServicesOptions,
            ]}
            mobileOptions={[
              ...locationOptions,
              {
                label: "Solutions",
                title: "Solutions",
                options:
                  solutionBuckets?.map((i) => ({
                    label: i.key,
                    title: i.key,
                    value: `solution:${i.key}`,
                  })) || [],
              },
              ...rentalServicesOptions,
            ]}
          />
        </Col>
      </FilterRow>
    </div>
  );
};
